import React, { useEffect, useMemo, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../../components/Button'
import { PayoutType } from './payoutTypes'
import { ManagedPaymentMethod } from '../../../util/Managed'
import Radom, { Customer, ExternalAccount, IPriceQuote, Payout, PayoutFeeEstimate, PayoutFundingCurrency, PayoutQuoteSide } from '../../../state/Radom'
import WarningBox from './WarningBox'
import { SectionBreakerHorizontal } from '../../../components/Shared'
import SummaryRow from './SummaryRow'
import DetailsRow from './DetailsRow'
import { Currency } from '../../../util/Currencies'
import CopyButton from '../../../components/CopyButton'
import HedgedPayoutCostBreakdown from './HedgedPayoutCostBreakdown'

interface IPayoutReviewProps {
  payoutType: PayoutType
  testMode: boolean
  isHedged: boolean
  fundingCurrency: PayoutFundingCurrency
  payoutSide: PayoutQuoteSide
  hedgedQuote: Payout | null
  onQuoteExpired: () => void
  selectedFundingMethod: ManagedPaymentMethod | undefined
  selectedPayoutMethod: ManagedPaymentMethod | Currency | undefined
  selectedCustomer: Customer | null
  onContinue: () => void
  onBack: () => void
  payoutAmount: string
  networkName?: string
  isSubmitting?: boolean
  // External account for both fiat and crypto
  selectedExternalAccount?: ExternalAccount
  // Fee estimate
  feeEstimate: PayoutFeeEstimate | null
}

const PayoutReview = ({
  payoutType,
  testMode,
  isHedged,
  fundingCurrency,
  payoutSide,
  hedgedQuote,
  onQuoteExpired,
  selectedFundingMethod,
  selectedPayoutMethod,
  selectedCustomer,
  onContinue,
  onBack,
  payoutAmount,
  networkName,
  isSubmitting,
  selectedExternalAccount,
  feeEstimate
}: IPayoutReviewProps): JSX.Element => {
  const payoutCurrency = (payoutType === PayoutType.Fiat || isHedged) && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker
    : selectedFundingMethod?.ticker || ''

  const hedgedDestinationMethod = isHedged && selectedPayoutMethod && !('icon' in selectedPayoutMethod)
    ? selectedPayoutMethod
    : undefined
  const hedgedDestinationNetwork = hedgedDestinationMethod
    ? hedgedDestinationMethod.hostChain?.name ?? hedgedDestinationMethod.name
    : undefined

  // Determine beneficiary display
  const beneficiaryName = selectedCustomer?.name || selectedCustomer?.companyName || ''
  const beneficiaryEmail = selectedCustomer?.email || ''

  const getDestinationDisplay = (): string => {
    if (!selectedExternalAccount) return '-'
    if (payoutType === PayoutType.Crypto) {
      const addr = selectedExternalAccount.crypto?.address || ''
      return addr.length > 12 ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : addr
    }
    // Fiat: use external account
    if (selectedExternalAccount.iban) {
      return `IBAN ****${selectedExternalAccount.iban.last4}`
    }
    if (selectedExternalAccount.account) {
      return `US ****${selectedExternalAccount.account.last4}`
    }
    return selectedExternalAccount.accountOwnerName
  }

  const formatFeeAmount = (value: number): string => {
    if (value === 0) return '0'
    return Number(value).toFixed(2)
  }

  const formatPercent = (value: number): string => {
    return `${(Number(value) * 100).toFixed(2)}%`
  }

  const formatUsdcTotal = (value: number): string => {
    if (value === 0) return '0'
    return value.toFixed(6)
  }

  const isFiat = payoutType === PayoutType.Fiat
  const ticker = (isFiat || isHedged) ? payoutCurrency : (selectedFundingMethod?.ticker || '')
  // Unit the payout is funded in (fiat payouts fund from USDC/USDT balances).
  const fundingTicker = isHedged ? fundingCurrency : (selectedFundingMethod?.ticker || 'USDC')

  const [quoteSecondsRemaining, setQuoteSecondsRemaining] = useState(0)

  useEffect(() => {
    if (!hedgedQuote) {
      setQuoteSecondsRemaining(0)
      return
    }
    const expiry = new Date(hedgedQuote.expiresAt).getTime()
    const tick = (): void => {
      const secs = Math.max(0, Math.ceil((expiry - Date.now()) / 1000))
      setQuoteSecondsRemaining(secs)
      if (secs <= 0) {
        onQuoteExpired()
      }
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [hedgedQuote])

  const isSourceSide = isHedged && payoutSide === 'from'
  const hedgedRecipientAmount = hedgedQuote?.cryptoEntries?.[0]?.amount ?? null
  const hedgedEntryHedge = hedgedQuote?.cryptoEntries?.[0]?.hedge

  const quoteToCurrency = isFiat && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker.toUpperCase()
    : 'USD'

  const [priceQuote, setPriceQuote] = useState<IPriceQuote | null>(null)

  useEffect(() => {
    Radom.getPriceQuotes([isFiat ? fundingTicker : 'USDC'], [quoteToCurrency])
      .then(quotes => { if (quotes.length > 0) setPriceQuote(quotes[0]) })
      .catch(err => console.error('Failed to fetch price quote', err))
  }, [quoteToCurrency, isFiat, fundingTicker])

  const totalUsdcRequired = useMemo((): number | null => {
    if (!feeEstimate || !payoutAmount || Number(payoutAmount) <= 0) return null
    const fee = Number(feeEstimate.estimatedFeeUsd) || 0
    if (isFiat) {
      if (feeEstimate.estimatedTotalDebitUsd !== undefined && feeEstimate.estimatedTotalDebitUsd !== null) {
        return Number(feeEstimate.estimatedTotalDebitUsd)
      }

      if (!priceQuote) return null
      const rate = Number(priceQuote.value)
      if (!rate || isNaN(rate)) return null
      return Number(payoutAmount) / rate + fee
    }
    // What leaves the balance at confirm is the reserve (estimate plus
    // padding); the unused part comes back once the payout is sent.
    const networkFee = Number(feeEstimate.reservedFeeUsd ?? feeEstimate.networkFeeUsd) || 0
    return Number(payoutAmount) + networkFee
  }, [feeEstimate, payoutAmount, isFiat, priceQuote])

  const customerFacingFiatRate = useMemo((): number | null => {
    if (!isFiat || !feeEstimate || !payoutAmount || Number(payoutAmount) <= 0) return null
    if (feeEstimate.exchangeRate !== undefined && feeEstimate.exchangeRate !== null) {
      return Number(feeEstimate.exchangeRate)
    }
    if (!feeEstimate.estimatedFundingAmountUsd) return null
    const fundingAmount = Number(feeEstimate.estimatedFundingAmountUsd)
    const recipientAmount = Number(payoutAmount)
    if (!fundingAmount || !recipientAmount || isNaN(fundingAmount) || isNaN(recipientAmount)) return null
    return recipientAmount / fundingAmount
  }, [feeEstimate, isFiat, payoutAmount])

  return (
    <div style={{
      maxWidth: 560,
      width: '100%',
      display: 'flex',
      padding: 28,
      flexDirection: 'column',
      borderRadius: 22,
      border: '1px solid var(--surface-pressed, #edf1f7)',
      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.035)',
      gap: 22,
      background: 'var(--surface, #ffffff)'
    }}>
      <h1 style={{ fontSize: 18.5, fontWeight: 500, lineHeight: '24px', margin: 0 }}>Confirm Payout</h1>
      <div style={{ display: 'flex', flexDirection: 'column', padding: 22, gap: 16, background: 'var(--surface-subtle, #fbfcff)', borderRadius: 18, border: '1px solid var(--surface-pressed, #edf1f7)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 11.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Recipient</span>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 16, fontWeight: 500, lineHeight: '24px' }}>{beneficiaryName || '-'}</span>
            <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 14, lineHeight: '20px', fontWeight: 400 }}>{beneficiaryEmail}</span>
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 400, lineHeight: '18px', display: 'flex', flexDirection: 'column', padding: 14, gap: 5, borderRadius: 14, background: 'var(--surface, #ffffff)', border: '1px solid var(--surface-pressed, #edf1f7)' }}>
            {payoutType === PayoutType.Crypto
              ? selectedExternalAccount?.crypto
                ? <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--ink, #20242d)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
                      {selectedExternalAccount.crypto.address}
                    </span>
                    <CopyButton text={selectedExternalAccount.crypto.address} />
                  </div>
                  <span style={{ color: 'var(--icon-muted, #747894)' }}>Network: {selectedExternalAccount.crypto.network}</span>
                </>
                : <span>-</span>
              : selectedExternalAccount
                ? <>
                  <span>{selectedExternalAccount.accountOwnerName}</span>
                  <span style={{ color: 'var(--icon-muted, #747894)' }}>{getDestinationDisplay()}</span>
                  {selectedExternalAccount.bankName && <span style={{ color: 'var(--icon-muted, #747894)' }}>Bank: {selectedExternalAccount.bankName}</span>}
                  {selectedExternalAccount.iban?.bic && <span style={{ color: 'var(--icon-muted, #747894)' }}>BIC: {selectedExternalAccount.iban.bic}</span>}
                </>
                : <span>-</span>
            }
          </div>
        </div>
        <SectionBreakerHorizontal/>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <DetailsRow title="Source" value={isHedged
            ? `${fundingCurrency} (Ethereum)`
            : `${selectedFundingMethod?.ticker || 'USDC'} (${networkName || 'Ethereum'})`} />
          <DetailsRow title="Destination" value={isHedged
            ? `${payoutCurrency} (${hedgedDestinationNetwork || ''})`
            : payoutType === PayoutType.Crypto
              ? `${selectedFundingMethod?.ticker || ''} (${networkName || ''})`
              : `${payoutCurrency} (Bank Transfer)`
          } />
          {!isHedged && (
            <DetailsRow title="Amount" value={`${payoutAmount} ${ticker}`} />
          )}
          {isFiat && (
            <DetailsRow title="Payout Currency" value={payoutCurrency} />
          )}
          {isFiat && (
            <DetailsRow
              title="Asset Exchange rate"
              value={customerFacingFiatRate ? `1 ${fundingTicker} = ${customerFacingFiatRate.toFixed(4)} ${payoutCurrency.toUpperCase()}` : 'Calculating...'}
            />
          )}

          {!isHedged && (
            <>
              <DetailsRow title="Estimated arrival" value="Instant" />
              {feeEstimate && (
                <>
                  {isFiat
                    ? <>
                      {feeEstimate.flatFeeUsd > 0 && (
                        <DetailsRow title="Flat Fee" value={`${formatFeeAmount(feeEstimate.flatFeeUsd)} ${fundingTicker}`} />
                      )}
                      {feeEstimate.minimumFeeUsd !== undefined && (
                        <DetailsRow title="Minimum Payout Fee" value={`${formatFeeAmount(feeEstimate.minimumFeeUsd)} ${fundingTicker}`} />
                      )}
                      {feeEstimate.feePercent > 0 && (
                        <DetailsRow title="Payout Fee Rate" value={formatPercent(feeEstimate.feePercent)} />
                      )}
                      <DetailsRow title="Payout Fee" value={`${formatFeeAmount(feeEstimate.estimatedFeeUsd)} ${fundingTicker}`} />
                      <DetailsRow title="Total deducted from balance" value={totalUsdcRequired !== null ? `${formatUsdcTotal(totalUsdcRequired)} ${fundingTicker}` : '-'} />
                    </>
                    : (
                      <>
                        {feeEstimate.networkFeeUsd !== undefined && feeEstimate.networkFeeUsd !== null && (
                          <DetailsRow title="Network Fee (estimated)" value={`${formatFeeAmount(feeEstimate.networkFeeUsd)} ${ticker}`} />
                        )}
                        {feeEstimate.reservedFeeUsd !== undefined && feeEstimate.reservedFeeUsd !== null && (
                          <DetailsRow
                            title="Reserved for the fee"
                            value={`${formatFeeAmount(feeEstimate.reservedFeeUsd)} USD · settled on the actual network cost once sent, the rest is refunded`}
                          />
                        )}
                      </>
                    )
                  }
                </>
              )}
              <SummaryRow
                title={payoutType === PayoutType.Crypto ? 'Total Sent' : 'Recipient Receives'}
                value={feeEstimate
                  ? `${formatFeeAmount(feeEstimate.estimatedRecipientAmount)} ${ticker}`
                  : `${payoutAmount} ${ticker}`
                }
              />
            </>
          )}
          {isHedged && !hedgedQuote && (
            <SummaryRow
              title={isSourceSide ? 'You pay' : 'Recipient receives'}
              value={`${payoutAmount} ${isSourceSide ? fundingCurrency : ticker}`}
            />
          )}
        </div>
      </div>

      {isHedged && hedgedQuote && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 16,
          gap: 12,
          background: '#fafbfc',
          borderRadius: 14,
          border: '1px solid var(--border-soft, #eceff3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink, #20242d)' }}>Quote</span>
            <span style={{
              fontSize: 12,
              color: quoteSecondsRemaining <= 10 ? '#c2410c' : 'var(--icon-muted, #747894)'
            }}>
              {quoteSecondsRemaining}s left
            </span>
          </div>
          {hedgedEntryHedge
            ? <HedgedPayoutCostBreakdown
              hedge={hedgedEntryHedge}
              cryptoTicker={ticker}
              recipientAmount={hedgedRecipientAmount ?? undefined}
              compact
            />
            : <SummaryRow
              title="Recipient receives"
              value={hedgedRecipientAmount ? `${hedgedRecipientAmount} ${ticker}` : `${payoutAmount} ${ticker}`}
            />
          }
        </div>
      )}

      {isHedged && !hedgedQuote && (
        <span style={{ fontSize: 13, color: 'var(--icon-muted, #747894)', lineHeight: '18px' }}>
          The rate is locked for 30 seconds after you request a quote.
        </span>
      )}

      <WarningBox payoutType={payoutType} />
      <div style={{ display: 'flex', width: '100%', gap: 12 }}>
        <SecondaryButton onClick={onBack} disabled={isSubmitting} style={{ width: '100%' }} >
            Back
        </SecondaryButton>

        <PrimaryButton
          onClick={onContinue}
          disabled={isSubmitting || (!isHedged && (!feeEstimate || totalUsdcRequired === null))}
          style={{ width: '100%', opacity: isSubmitting ? 0.6 : 1 }} >
          {isSubmitting
            ? 'Submitting...'
            : isHedged
              ? hedgedQuote
                ? `Confirm payout (${quoteSecondsRemaining}s)`
                : 'Get locked quote'
              : 'Confirm & Send'}
        </PrimaryButton>
      </div>

    </div>

  )
}

export default PayoutReview
